import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import {
    AdvancedSearchEntityType,
    LayoutSectionKey,
    LayoutSectionParentKey,
    useGetEntitiesQuery,
    useGetLayoutSectionsQuery,
} from "generated/graphql";
import React from "react";
import { useBridgeQuestions } from "store/selectors/bridge";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { stableQueryOptions } from "util/requests";
import {
    LayoutItemExperience,
    LayoutSectionItemSettings,
} from "../layout-section-item-settings";
import { ReferralPipelinesSettings } from "./agency-settings-referral-pipelines";
import { AgencyTogglePref } from "./agency-settings-toggle";
import { EntitySettings } from "./customize/entity-settings";
import { ReferralTierEditor } from "./referral-tier-editor";

export const AgencySettingsReferrals: React.FC<unknown> = () => {
    const isPlacementEnabled = useFeature("placement");
    const referralTiersFeatureEnabled = useFeature("referralTiers");
    const { settings } = useTenantSettings();
    const entitiesQuery = useGetEntitiesQuery({}, stableQueryOptions());
    const layoutSections = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.ReferralCommunityCard },
        stableQueryOptions(),
    );

    const questions = useBridgeQuestions(AdvancedSearchEntityType.Community);

    const cardSectionId = layoutSections.data?.getLayoutSections?.layoutSections?.find(
        s => s.sectionKey === LayoutSectionKey.ReferralCommunityCardKeyAttributes,
    )?.id;

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Referrals</ContentHeader>
                <div className="space-y-4">
                    <AgencyTogglePref
                        field="ownedReferralSources"
                        label="Referral sources are assignable to users"
                        description="Allow referral sources (both businesses and contacts) to be assigned to 1 user within the organization."
                    />
                </div>
            </ContentFull>
            <ReferralPipelinesSettings />
            {referralTiersFeatureEnabled ? (
                <ContentFull>
                    <ContentHeader>Referral source tiers</ContentHeader>
                    <div className="space-y-4">
                        <AgencyTogglePref
                            field="referralTiersEnabled"
                            label="Enable referral source tiers"
                            description="Rate referral sources and communities with a tenant-wide tier scale. Contacts inherit their organization's tier unless overridden."
                        />
                        {settings.referralTiersEnabled ? <ReferralTierEditor /> : null}
                    </div>
                </ContentFull>
            ) : null}
            {isPlacementEnabled ? (
                <ContentFull>
                    <ContentHeader>Referral search</ContentHeader>
                    <div className="space-y-4">
                        {cardSectionId ? (
                            <LayoutSectionItemSettings
                                entityMetadataName="Community"
                                includeStandardFields={true}
                                questions={questions}
                                questionFilter={_ => true}
                                questionTitle={q => q.title}
                                categoryFilter={d => d.communityEnabled}
                                categoryTitle={d => d.communityHeading}
                                layoutSectionId={cardSectionId}
                                label="Community fields to show on referral search results:"
                                mode="alwaysEditing"
                                relations={[]}
                                itemExperience={LayoutItemExperience.communityCard}
                            />
                        ) : null}
                    </div>
                </ContentFull>
            ) : null}
            <QueryRenderer query={entitiesQuery} name="Settings.getEntities">
                {data => (
                    <>
                        <ContentFull>
                            <ContentHeader>Customize Organizations</ContentHeader>

                            <EntitySettings
                                entityName="ReferenceBusiness"
                                entities={data.getEntities}
                            />
                        </ContentFull>

                        <ContentFull>
                            <ContentHeader>Customize Contacts</ContentHeader>

                            <EntitySettings
                                entityName="ReferenceContact"
                                entities={data.getEntities}
                            />
                        </ContentFull>
                    </>
                )}
            </QueryRenderer>
        </div>
    );
};
